import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Formik, FormikProps } from "formik";
import moment from "moment";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "../../../../ui-components/table/table";
import { TagColor } from "../../../../app/data/common/tagColor";
import XGSErrorMessage from "../../../../ui-components/error-message/errorMessage";
import Tag from "../../../../ui-components/molecules/tag/tag";
import { LabelModes } from "../../../../ui-components/molecules/labeled-inputs/labeledInput";
import XGSFormInput from "../../../../ui-components/form/input/xgsFormInput";
import Button, { ButtonThemes } from "../../../../ui-components/button/button";
import "./related-events-logs.scss"
import "../image-history-logs/image-history-logs.scss";
import "../../podRecognition/podRecognition.scss";
import { RelatedEventsLogsFilterModel, RelatedEventsLogsFilterSchema, RelatedEventsLogsResponseItemModel, RelatedEventsLogsResponseModel } from "../../../../app/data/documents/relatedEventsLogsModels";
import { getRelatedEventsLogs, relatedEventsLogsSelector } from "../../../../slices/documents/relatedEventsLogsSlice";
import { getDocTypeTag, getIndexTag } from "../image-history-logs/filter-options";
import { ReactComponent as LinkIcon } from "../../../../images/link.svg";
import ReactViewer from "react-viewer";

const initialFilterValues: RelatedEventsLogsFilterModel = {
    eventId: ""
};
export const ProgramLogsStatuses = [
    {
        label: "failure",
        value: false
    },
    {
        label: "success",
        value: true
    }
];
export const ProgramHaltOptions = [
    {
        label: "No",
        value: false
    },
    {
        label: "Yes",
        value: true
    }
];
export const ProgramHaltVarifyOptions = [
    {
        label: "N",
        value: "No"
    },
    {
        label: "Y",
        value: "Yes"
    }
];

const columnHelper = createColumnHelper<RelatedEventsLogsResponseItemModel>();
const columnChildHelper = createColumnHelper<RelatedEventsLogsResponseModel>();
interface RelatedEventsLogsProps {
    filterFormRef: React.RefObject<FormikProps<RelatedEventsLogsFilterModel>>;
}
const RelatedEventsLogs: React.FC<RelatedEventsLogsProps> = ({ filterFormRef }) => {
    const relatedEventsLogsState = useSelector(relatedEventsLogsSelector);
    const dispatch = useDispatch();
    const [expandedRow, setExpandedRow] = useState<string | null>(null);
    const [popupPosition, setPopupPosition] = useState<{ top: number, left: number, width: number, height: number } | null>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    const [popupHeight, setPopupHeight] = useState<number>(0);
    const columnRef = useRef<HTMLDivElement | null>(null);
    const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
    const tableRef = React.useRef<HTMLElement>(null);
    const [showGallery, setShowGallery] = useState<boolean>(false);
    const [galleryImages, setGalleryImages] = useState<any>([]);
    const [lastPage, setLastPage] = useState<boolean>(false);
    const [filter, setFilter] = useState<RelatedEventsLogsFilterModel>(initialFilterValues);

    useEffect(() => {
        if (popupRef.current) {
            const height = popupRef.current.getBoundingClientRect().height;
            setPopupHeight(height);
        }
    }, [expandedRow, popupPosition]);

    useEffect(() => {
        if (relatedEventsLogsState.recordsRequest?.last === relatedEventsLogsState.recordsRequest?.page)
            setLastPage(true);
        else
            setLastPage(false);

        setGalleryImages(
            relatedEventsLogsState.recordItems.map((record: any, index: number) => {
                return {
                    src: record.image,
                    class: `xgs-related-events-logs__image-styles-${index}`,
                    rotation: 0,
                    flipHorizontal: false,
                    flipVertical: false,
                    zoomed: false,
                };
            })
        );
    // eslint-disable-next-line
    }, [relatedEventsLogsState.recordItems]);

    const handleShowMore = (event: React.MouseEvent<HTMLDivElement>, rowContent: string | null, topMinus: number) => {
        const cell = event.currentTarget.getBoundingClientRect();
        setPopupPosition({
            top: cell.top + window.scrollY - topMinus,
            left: cell.left + window.scrollX - 216,
            width: cell.width,
            height: cell.height,
        });
        setExpandedRow(rowContent);
    };

    const handleClosePopup = () => {
        setExpandedRow(null);
        setPopupPosition(null);
    };

    useEffect(() => {
        const tableElement = tableRef.current?.getElementsByClassName("xgs-table__fixed-size-list")?.[0];
        const handleScroll = () => {
            setExpandedRow(null);
        };

        const currentRef = tableElement;

        if (currentRef) {
            currentRef.addEventListener('scroll', handleScroll);
        }

        return () => {
            if (currentRef) {
                currentRef.removeEventListener('scroll', handleScroll);
            }
        };
        // eslint-disable-next-line
    }, [tableRef.current?.getElementsByClassName("xgs-table__fixed-size-list")?.[0]]);

    const columns = [
        columnHelper.accessor("probill", {
            id: "probill",
            header: "Probill",
            cell: ({ row }) => (
                <>
                    {row.original.probill && row.original.probill !== '0' ? (
                        <>
                            {row.original.probill}
                        </>
                    ) : (<strong className="text-light">N/A</strong>)}
                </>
            ),
            size: 130,
            minSize: 130
        }),
        columnHelper.accessor("failedeventsCount", {
            id: "failedeventsCount",
            header: "Failed Events Count",
            cell: (cellProps) => {
                return (
                    <Tag mods={{ color: cellProps.getValue().toString() === '0' ? TagColor.GREY : TagColor.RED, uppercase: true }}>
                        {cellProps.getValue()}
                    </Tag>
                );
            },
            size: 125,
            minSize: 125
        }),
        columnHelper.accessor("bol", {
            id: "bol",
            header: "BOL",
            cell: ({ row }) => (
                <>
                    {row.original.bol && row.original.bol !== '0' ? (
                        <>
                            {row.original.bol}
                        </>
                    ) : (<strong className="text-light">N/A</strong>)}
                </>
            ),
            size: 130,
            minSize: 130
        }),
        columnHelper.accessor("po", {
            id: "po",
            header: "PO",
            cell: ({ row }) => (
                <>
                    {row.original.po && row.original.po !== '0' ? (
                        <>
                            {row.original.po}
                        </>
                    ) : (<strong className="text-light">N/A</strong>)}
                </>
            ),
            size: 130,
            minSize: 130
        }),
        columnHelper.accessor("docId", {
            id: "docId",
            header: "Document ID",
            cell: ({ row }) => (
                <>
                    {row.original.docId && row.original.docId !== '0' ? (
                        <>
                            {row.original.docId}
                        </>
                    ) : (<strong className="text-light">N/A</strong>)}
                </>
            ),
            size: 125,
            minSize: 125
        }),
        columnHelper.accessor("docType", {
            id: "docType",
            header: "Doc Type",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                if (!value) return (
                    <span className="text-light xgs-table__cell-subitem">
                        <strong>N/A</strong>
                    </span>);

                return (
                    getDocTypeTag(cellProps.getValue())
                );
            },
            size: 50,
            minSize: 50
        }),
        columnHelper.accessor("docStatus", {
            id: "docStatus",
            header: "Index Type",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                if (!value) return (
                    <span className="text-light xgs-table__cell-subitem">
                        <strong>N/A</strong>
                    </span>);

                return (
                    getIndexTag(cellProps.getValue())
                );
            },
            size: 130,
            minSize: 130
        }),
        columnHelper.accessor("terminalName", {
            id: "terminalName",
            header: "Terminal",
            cell: (cellProps) => {
                const terminal = cellProps.row.original.terminal;
                return (
                    <div>
                        <div>
                            {cellProps.getValue()}
                            <span className="text-light xgs-table__cell-subitem">
                                {terminal ? (terminal) : (
                                    <strong>N/A</strong>
                                )}
                            </span>
                        </div>
                    </div>
                );
            },
            size: 170,
            minSize: 170
        }),
        columnHelper.accessor("customerName", {
            id: "customerName",
            header: "customer",
            cell: (cellProps) => {
                const customerNumber = cellProps.row.original.customerNumber;
                return (
                    <div>
                        <div>
                            {cellProps.getValue()}
                            <span className="text-light xgs-table__cell-subitem">
                                {customerNumber && `${customerNumber}` ? (customerNumber && `${customerNumber}`) : (
                                    <strong>N/A</strong>
                                )}
                            </span>
                        </div>
                    </div>
                );
            },
            size: 210,
            minSize: 210
        }),
        columnHelper.accessor("image", {
            id: "image",
            header: "Document Link",
            cell: ({ row }) => (
                <>
                    {row.original.image ? (
                        <>
                            <div className="xgs-image-history-logs__image-container" onClick={() => { setCurrentImageIndex(row.index); setShowGallery(true); }}>
                                <div className="xgs-image-history-logs__icon-wrapper">
                                    <div className="xgs-image-history-logs__icon" onClick={() => { setCurrentImageIndex(row.index); setShowGallery(true); }}>
                                        <LinkIcon />
                                    </div>
                                </div>
                                <div className="xgs-image-history-logs__open-image-text">Open Image</div>
                            </div>
                        </>
                    ) : (<strong className="text-light">N/A</strong>)}
                </>
            ),
            size: 112,
            minSize: 112
        })
    ];

    const groupColumns = [
        columnChildHelper.accessor("eventTime", {
            id: "eventTime",
            header: "Time",
            cell: (cellProps) => {
                const rawValue = cellProps.getValue() as string | undefined;

                if (!rawValue) return null;

                const date = moment(rawValue, "YYYY-MM-DD HH:mm:ss.SSSSSSSSS");

                return (
                    <div>
                        <div>
                            {date.format("MM/DD/YY")}
                            <span className="text-light xgs-table__cell-subitem">
                                {date.format("hh:mm:ss A")}
                            </span>
                        </div>
                    </div>
                );
            },
            size: 170,
            minSize: 170
        }),
        columnChildHelper.accessor("imgStatus", {
            id: "imgStatus",
            header: "Status",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                if (!value) return null;

                return (
                    <Tag mods={{ color: cellProps.getValue() === 'success' ? TagColor.GREEN : TagColor.RED, uppercase: true }}>
                        {cellProps.getValue()}
                    </Tag>
                );
            },
            size: 80,
            minSize: 80
        }),
        columnChildHelper.accessor("isPgmHalt", {
            id: "isPgmHalt",
            header: "Program Halt",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                if (!value) return null;

                return (
                    <Tag mods={{ color: cellProps.getValue() === 'N' ? TagColor.GREEN : TagColor.RED, uppercase: true }}>
                        {cellProps.getValue()}
                    </Tag>
                );
            },
            size: 110,
            minSize: 110
        }),
        columnChildHelper.accessor("event", {
            id: "event",
            header: "Action",
            size: 120,
            minSize: 120
        }),
        columnChildHelper.accessor("eventSource", {
            id: "eventSource",
            header: "Source",
            size: 120,
            minSize: 120
        }),
        columnChildHelper.accessor("eventDetails", {
            id: "eventDetails",
            header: "Event Details",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                const showMoreVisible = value.length > 65;

                return (
                    <div ref={columnRef} className="xgs-image-history-logs__event-details-cell"
                        onMouseEnter={(e) => {
                            if (showMoreVisible) {
                                handleShowMore(e, value ? value : null, 30);
                            }
                        }}
                        onMouseLeave={handleClosePopup}
                    >
                        {showMoreVisible ? (
                            <>
                                {value?.substring(0, 65)}...
                                <div
                                    onClick={(e: any) => handleShowMore(e, value ? value : null, 42)}
                                    className="xgs-image-history-logs__show-more-less-btn"
                                >
                                    Show More
                                </div>
                            </>
                        ) : (
                            <span>{value}</span>
                        )}
                    </div>
                );
            },
            size: 545,
            minSize: 545
        }),
        columnChildHelper.accessor("associatedUser", {
            id: "associatedUser",
            header: "Associated User",
            cell: ({ row }) => (
                <>
                    {row.original.associatedUser ? (
                        <>
                            {row.original.associatedUser}
                        </>
                    ) : (<strong className="text-light">N/A</strong>)}
                </>
            ),
            size: 150,
            minSize: 150
        }),
        columnChildHelper.accessor("associatedEmail", {
            id: "associatedEmail",
            header: "Associated Email",
            cell: ({ row }) => (
                <>
                    {row.original.associatedEmail ? (
                        <>
                            {row.original.associatedEmail}
                        </>
                    ) : (<strong className="text-light">N/A</strong>)}
                </>
            ),
            size: 150,
            minSize: 150
        }),
        columnChildHelper.accessor("eventId", {
            id: "eventId",
            header: "Event ID",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                const isFiltered = value === filter.eventId;

                return (
                    <span style={{ color: isFiltered ? 'red' : 'inherit' }}>
                        {value}
                    </span>
                );
            },
            size: 110,
            minSize: 110
        }),
    ];

    const onSubmitFilter = (values: RelatedEventsLogsFilterModel) => {
        setFilter(values);
        dispatch(getRelatedEventsLogs(values, 1));
    };

    const onListInfiniteScroll = () => {
        if (relatedEventsLogsState.recordsRequest?.last === relatedEventsLogsState.recordsRequest?.page) return;
        dispatch(getRelatedEventsLogs(filter, relatedEventsLogsState.recordsRequest ? relatedEventsLogsState.recordsRequest.page + 1 : 1));
    };

    return (
        <div style={{ marginRight: "34px", marginTop: "24px" }}>
            <Formik
                initialValues={initialFilterValues}
                onSubmit={onSubmitFilter}
                innerRef={filterFormRef}
                validationSchema={RelatedEventsLogsFilterSchema}
            >
                {(props: FormikProps<RelatedEventsLogsFilterModel>) => (
                    <Form>
                        <div className="xgs-related-events-logs">
                            <div className="xgs-related-events-logs__list__filters">
                                <XGSFormInput
                                    type="text"
                                    name="Event Number"
                                    label="Event ID:"
                                    value={props.values.eventId}
                                    onChange={(e) => props.setFieldValue("eventId", e.currentTarget.value)}
                                    labelMode={LabelModes.column}
                                    className="xgs-related-events-logs__list__filters__input"
                                />
                                <Button
                                    type="submit"
                                    theme={ButtonThemes.blue}
                                    className="xgs-related-events-logs__list__filters__button"
                                    spinner={relatedEventsLogsState.requestStarted && relatedEventsLogsState.requestCreator === "GET_RECORDS"}
                                    disabled={!props.isValid || relatedEventsLogsState.requestStarted || !Object.values(props.values).some((value) => value && value.toString().trim() !== "")}
                                >
                                    Search
                                </Button>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>
            {(relatedEventsLogsState.requestFailed && (relatedEventsLogsState.requestCreator === "GET_RECORDS")) && (
                <XGSErrorMessage>{relatedEventsLogsState.requestError}</XGSErrorMessage>
            )}
            {!(relatedEventsLogsState.requestFailed && relatedEventsLogsState.requestCreator === "GET_RECORDS") && (
                <div className="xgs-image-history-logs__list">
                    <Table
                        ref={tableRef}
                        sorting={true}
                        isLoading={relatedEventsLogsState.requestStarted && relatedEventsLogsState.requestCreator === "GET_RECORDS"}
                        keepTableOnLoading
                        columns={columns}
                        data={relatedEventsLogsState.recordItems}
                        rowHeight={56}
                        minTableHeight={240}
                        noResultsText="There are no entries"
                        responsive
                        infiniteScroll
                        infiniteScrollLoading={relatedEventsLogsState.requestStarted && relatedEventsLogsState.requestCreator === "GET_RECORDS_PORTION"}
                        infiniteScrollHasNext={!lastPage}
                        onInfiniteScroll={onListInfiniteScroll}
                        subRowsField="events"
                        subTableProps={{
                            columns: groupColumns,
                            sorting: true,
                        }}
                    />
                </div>
            )}
            {expandedRow !== null && popupPosition && (
                <div
                    ref={popupRef}
                    className="xgs-image-history-logs__event-details-popup"
                    style={{
                        position: "absolute",
                        top: popupPosition.top - popupHeight,
                        left: popupPosition.left,
                        width: popupPosition.width
                    }}
                    onMouseLeave={handleClosePopup}
                >
                    <div className="xgs-image-history-logs__popup-content">
                        <p>{expandedRow || "No details available"}</p>
                        <button
                            onClick={handleClosePopup}
                            className="xgs-image-history-logs__show-more-less-btn"
                        >
                            Show Less <i className="fas fa-minus"></i>
                        </button>
                    </div>
                </div>
            )}
            <ReactViewer
                visible={showGallery}
                onClose={() => setShowGallery(false)}
                images={galleryImages.map((image: any) => ({
                    src: image.src,
                    alt: "Gallery Image"
                }))}
                defaultScale={1.2}
                attribute={false}
                activeIndex={currentImageIndex}
            />
        </div>
    );
};

export default RelatedEventsLogs;